/**
 * qEEG trend transform helpers — DSA, aEEG, asymmetry, rhythmicity,
 * spectral edge, bandpower, ratio lanes, and generation provenance.
 *
 * Algorithm version is bumped when numerical outputs change.
 */

export const QEEG_TREND_ALGORITHM_VERSION = "qeeg-trends-v1.3.0-s2";

export type QEEGTrendType =
  | "spectrogram"
  | "aeeg"
  | "amplitude"
  | "asymmetry"
  | "rhythmicity"
  | "suppression"
  | "spectral_edge"
  | "bandpower"
  | "ratio"
  | "seizure_burden"
  | "vs_baseline"
  | "zscore_bandpower";

export const QEEG_TREND_TYPES: QEEGTrendType[] = [
  "spectrogram",
  "aeeg",
  "amplitude",
  "asymmetry",
  "rhythmicity",
  "suppression",
  "spectral_edge",
  "bandpower",
  "ratio",
  "seizure_burden",
  "vs_baseline",
  "zscore_bandpower",
];

export const QEEG_TREND_LABELS: Record<QEEGTrendType, string> = {
  spectrogram: "FFT / DSA",
  aeeg: "aEEG",
  amplitude: "Amplitude",
  asymmetry: "Asymmetry",
  rhythmicity: "Rhythmicity",
  suppression: "Suppression",
  spectral_edge: "Spectral edge",
  bandpower: "Bandpower",
  ratio: "Power ratio",
  seizure_burden: "Seizure burden",
  vs_baseline: "vsBaseline",
  zscore_bandpower: "Normative z-score",
};

export type QEEGBandName = "Delta" | "Theta" | "Alpha" | "Beta" | "Gamma" | "Sigma";

export const QEEG_BAND_RANGES: Record<QEEGBandName, [number, number]> = {
  Delta: [0.5, 4],
  Theta: [4, 8],
  Alpha: [8, 13],
  /** Sleep spindle band (11–16 Hz) for STR biomarkers. */
  Sigma: [11, 16],
  Beta: [13, 30],
  Gamma: [30, 45],
};

export type QEEGTrendSample = {
  timeSeconds: number;
  value: number;
  /** Optional secondary value (e.g. aEEG lower margin). */
  secondaryValue?: number;
};

export type QEEGTrendWindow = {
  startSeconds: number;
  endSeconds: number;
  durationSeconds: number;
};

export type QEEGFilterProvenance = {
  highPassHz: number | null;
  lowPassHz: number | null;
  notch50Hz: boolean;
  notch60Hz: boolean;
};

export type QEEGTrendProvenance = {
  algorithmVersion: string;
  channelSet: string[];
  montage: string | null;
  samplingRateHz: number;
  filters: QEEGFilterProvenance;
  artifactExclusions: string[];
  epochSeconds: number;
  generatedAtIso: string;
};

export type BuildQEEGTrendProvenanceInput = {
  channelSet?: string[];
  montage?: string | null;
  samplingRateHz: number;
  filters?: Partial<QEEGFilterProvenance>;
  artifactExclusions?: string[];
  epochSeconds?: number;
  generatedAt?: Date;
};

const BAND_RANGES = QEEG_BAND_RANGES;

export function resolveTrendWindow(
  currentTimeSeconds: number,
  halfWindowSeconds: number,
  recordingDurationSeconds: number,
): QEEGTrendWindow {
  const half = Math.max(1, halfWindowSeconds);
  const start = Math.max(0, currentTimeSeconds - half);
  const end = Math.min(
    Math.max(recordingDurationSeconds, start + 1),
    currentTimeSeconds + half,
  );
  return {
    startSeconds: start,
    endSeconds: end,
    durationSeconds: Math.max(0.001, end - start),
  };
}

export function buildQEEGTrendProvenance(
  input: BuildQEEGTrendProvenanceInput,
): QEEGTrendProvenance {
  return {
    algorithmVersion: QEEG_TREND_ALGORITHM_VERSION,
    channelSet: [...(input.channelSet || [])],
    montage: input.montage ?? null,
    samplingRateHz: Math.max(1, input.samplingRateHz),
    filters: {
      highPassHz: input.filters?.highPassHz ?? null,
      lowPassHz: input.filters?.lowPassHz ?? null,
      notch50Hz: Boolean(input.filters?.notch50Hz),
      notch60Hz: Boolean(input.filters?.notch60Hz),
    },
    artifactExclusions: [...(input.artifactExclusions || [])],
    epochSeconds: Math.max(0.1, input.epochSeconds ?? 1),
    generatedAtIso: (input.generatedAt ?? new Date()).toISOString(),
  };
}

function toArray(samples: Float32Array | number[]): number[] {
  return Array.isArray(samples) ? samples.map(Number) : Array.from(samples);
}

function epochIterate(
  samples: Float32Array | number[],
  sampleRate: number,
  epochSeconds: number,
  fn: (epoch: number[], timeSeconds: number) => void,
): void {
  const rate = Math.max(1, sampleRate);
  const epochSize = Math.max(1, Math.round(rate * epochSeconds));
  for (let i = 0; i + epochSize <= samples.length; i += epochSize) {
    const epoch: number[] = [];
    for (let j = 0; j < epochSize; j += 1) {
      epoch.push(Number(samples[i + j] || 0));
    }
    fn(epoch, i / rate);
  }
}

/** RMS amplitude trend from channel samples. */
export function computeAmplitudeTrend(
  samples: Float32Array | number[],
  sampleRate: number,
  epochSeconds = 1,
): QEEGTrendSample[] {
  const out: QEEGTrendSample[] = [];
  epochIterate(samples, sampleRate, epochSeconds, (epoch, timeSeconds) => {
    let sumSq = 0;
    for (const v of epoch) sumSq += v * v;
    out.push({
      timeSeconds,
      value: Math.sqrt(sumSq / epoch.length),
    });
  });
  return out;
}

/**
 * Amplitude-integrated EEG (aEEG) style margins.
 * Upper = 90th percentile |amp|, lower = 10th percentile |amp|, log10 scale.
 */
export function computeAEEGTrend(
  samples: Float32Array | number[],
  sampleRate: number,
  epochSeconds = 2,
): QEEGTrendSample[] {
  const out: QEEGTrendSample[] = [];
  epochIterate(samples, sampleRate, epochSeconds, (epoch, timeSeconds) => {
    const envelope = epoch.map(Math.abs).sort((a, b) => a - b);
    const upper = envelope[Math.min(envelope.length - 1, Math.floor(envelope.length * 0.9))] || 0;
    const lower = envelope[Math.floor(envelope.length * 0.1)] || 0;
    out.push({
      timeSeconds,
      value: upper > 0 ? Math.log10(upper) : 0,
      secondaryValue: lower > 0 ? Math.log10(lower) : 0,
    });
  });
  return out;
}

/** Left-right power asymmetry index in [-1, 1]. */
export function computeAsymmetryIndex(
  leftPower: number,
  rightPower: number,
): number {
  const l = Math.max(0, leftPower);
  const r = Math.max(0, rightPower);
  const denom = l + r;
  if (denom <= 1e-12) return 0;
  return (l - r) / denom;
}

export function computeAsymmetryTrend(
  leftEpochPowers: number[],
  rightEpochPowers: number[],
  epochSeconds = 1,
): QEEGTrendSample[] {
  const n = Math.min(leftEpochPowers.length, rightEpochPowers.length);
  const out: QEEGTrendSample[] = [];
  for (let i = 0; i < n; i += 1) {
    out.push({
      timeSeconds: i * epochSeconds,
      value: computeAsymmetryIndex(leftEpochPowers[i], rightEpochPowers[i]),
    });
  }
  return out;
}

/**
 * Rhythmicity proxy: normalized peak autocorrelation lag energy in 0.5–12 Hz.
 * Returns samples in [0, 1].
 */
export function computeRhythmicityProxy(
  samples: Float32Array | number[],
  sampleRate: number,
  epochSeconds = 2,
): QEEGTrendSample[] {
  const rate = Math.max(1, sampleRate);
  const epochSize = Math.max(8, Math.round(rate * epochSeconds));
  const minLag = Math.max(1, Math.floor(rate / 12));
  const maxLag = Math.max(minLag + 1, Math.floor(rate / 0.5));
  const out: QEEGTrendSample[] = [];

  for (let start = 0; start + epochSize <= samples.length; start += epochSize) {
    let energy = 0;
    for (let i = 0; i < epochSize; i += 1) {
      const v = Number(samples[start + i] || 0);
      energy += v * v;
    }
    if (energy <= 1e-12) {
      out.push({ timeSeconds: start / rate, value: 0 });
      continue;
    }

    let best = 0;
    for (let lag = minLag; lag <= maxLag && lag < epochSize; lag += 1) {
      let corr = 0;
      const count = epochSize - lag;
      for (let i = 0; i < count; i += 1) {
        corr +=
          Number(samples[start + i] || 0) *
          Number(samples[start + i + lag] || 0);
      }
      best = Math.max(best, corr / energy);
    }
    out.push({
      timeSeconds: start / rate,
      value: Math.max(0, Math.min(1, best)),
    });
  }
  return out;
}

/**
 * Versioned burst-suppression ratio (BSR): fraction of RMS-envelope windows
 * below threshold per epoch (aligned with QEEGMetrics / S1 long-study).
 */
export function computeSuppressionRatio(
  samples: Float32Array | number[],
  sampleRate: number,
  thresholdUv = 5,
  epochSeconds = 1,
): QEEGTrendSample[] {
  const out: QEEGTrendSample[] = [];
  const rate = Math.max(1, sampleRate);
  const windowSize = Math.max(1, Math.floor(0.1 * rate));
  epochIterate(samples, sampleRate, epochSeconds, (epoch, timeSeconds) => {
    let suppressedWindows = 0;
    let totalWindows = 0;
    for (let i = 0; i < epoch.length; i += windowSize) {
      const slice = epoch.slice(i, i + windowSize);
      if (!slice.length) continue;
      const rms = Math.sqrt(
        slice.reduce((sum, v) => sum + v * v, 0) / slice.length,
      );
      totalWindows += 1;
      if (rms < thresholdUv) suppressedWindows += 1;
    }
    out.push({
      timeSeconds,
      value: totalWindows ? suppressedWindows / totalWindows : 0,
    });
  });
  return out;
}

/** @deprecated Use computeSuppressionRatio — kept for transitional imports. */
export function computeSuppressionRatioPlaceholder(
  samples: Float32Array | number[],
  sampleRate: number,
  thresholdUv = 5,
  epochSeconds = 1,
): QEEGTrendSample[] {
  return computeSuppressionRatio(
    samples,
    sampleRate,
    thresholdUv,
    epochSeconds,
  );
}

/**
 * Lightweight Goertzel-style band power estimate for trend epochs.
 * Avoids full FFT for long-recording UI performance.
 */
export function estimateBandPower(
  epoch: number[],
  sampleRate: number,
  band: [number, number],
): number {
  if (!epoch.length || sampleRate <= 0) return 0;
  const [lo, hi] = band;
  const center = (lo + hi) / 2;
  const coeff = 2 * Math.cos((2 * Math.PI * center) / sampleRate);
  let s0 = 0;
  let s1 = 0;
  let s2 = 0;
  for (const sample of epoch) {
    s0 = sample + coeff * s1 - s2;
    s2 = s1;
    s1 = s0;
  }
  const power = s1 * s1 + s2 * s2 - coeff * s1 * s2;
  return Math.max(0, power / epoch.length);
}

export function estimateBandPowers(
  epoch: number[],
  sampleRate: number,
): Record<QEEGBandName, number> {
  return {
    Delta: estimateBandPower(epoch, sampleRate, BAND_RANGES.Delta),
    Theta: estimateBandPower(epoch, sampleRate, BAND_RANGES.Theta),
    Alpha: estimateBandPower(epoch, sampleRate, BAND_RANGES.Alpha),
    Sigma: estimateBandPower(epoch, sampleRate, BAND_RANGES.Sigma),
    Beta: estimateBandPower(epoch, sampleRate, BAND_RANGES.Beta),
    Gamma: estimateBandPower(epoch, sampleRate, BAND_RANGES.Gamma),
  };
}

/** Spectral edge frequency (default SEF95) per epoch. */
export function computeSpectralEdgeTrend(
  samples: Float32Array | number[],
  sampleRate: number,
  epochSeconds = 2,
  percentile = 95,
): QEEGTrendSample[] {
  const rate = Math.max(1, sampleRate);
  const out: QEEGTrendSample[] = [];
  epochIterate(samples, sampleRate, epochSeconds, (epoch, timeSeconds) => {
    const n = epoch.length;
    // Coarse magnitude spectrum via DFT bins up to Nyquist (capped for perf).
    const binCount = Math.min(64, Math.floor(n / 2));
    const magnitudes: number[] = [];
    let total = 0;
    for (let k = 1; k <= binCount; k += 1) {
      let re = 0;
      let im = 0;
      const omega = (2 * Math.PI * k) / n;
      for (let t = 0; t < n; t += 1) {
        const v = epoch[t];
        re += v * Math.cos(omega * t);
        im -= v * Math.sin(omega * t);
      }
      const mag = (re * re + im * im) / (n * n);
      magnitudes.push(mag);
      total += mag;
    }
    if (total <= 1e-18) {
      out.push({ timeSeconds, value: 0 });
      return;
    }
    const threshold = (percentile / 100) * total;
    let cumulative = 0;
    let edgeHz = (binCount * rate) / n;
    for (let k = 0; k < magnitudes.length; k += 1) {
      cumulative += magnitudes[k];
      if (cumulative >= threshold) {
        edgeHz = ((k + 1) * rate) / n;
        break;
      }
    }
    out.push({ timeSeconds, value: edgeHz });
  });
  return out;
}

/** Absolute bandpower trend for a named clinical band. */
export function computeBandpowerTrend(
  samples: Float32Array | number[],
  sampleRate: number,
  band: QEEGBandName = "Alpha",
  epochSeconds = 2,
): QEEGTrendSample[] {
  const out: QEEGTrendSample[] = [];
  epochIterate(samples, sampleRate, epochSeconds, (epoch, timeSeconds) => {
    out.push({
      timeSeconds,
      value: estimateBandPower(epoch, sampleRate, BAND_RANGES[band]),
    });
  });
  return out;
}

export type QEEGPowerRatioKind =
  | "alpha_delta"
  | "theta_alpha"
  | "beta_alpha"
  | "delta_theta";

export const QEEG_POWER_RATIO_LABELS: Record<QEEGPowerRatioKind, string> = {
  alpha_delta: "α/δ",
  theta_alpha: "θ/α",
  beta_alpha: "β/α",
  delta_theta: "δ/θ",
};

/** Clinical power-ratio trends (α/δ, θ/α, β/α, δ/θ). */
export function computePowerRatioTrend(
  samples: Float32Array | number[],
  sampleRate: number,
  ratioKind: QEEGPowerRatioKind = "alpha_delta",
  epochSeconds = 2,
): QEEGTrendSample[] {
  const pair: Record<QEEGPowerRatioKind, [QEEGBandName, QEEGBandName]> = {
    alpha_delta: ["Alpha", "Delta"],
    theta_alpha: ["Theta", "Alpha"],
    beta_alpha: ["Beta", "Alpha"],
    delta_theta: ["Delta", "Theta"],
  };
  const [numBand, denBand] = pair[ratioKind];
  const out: QEEGTrendSample[] = [];
  epochIterate(samples, sampleRate, epochSeconds, (epoch, timeSeconds) => {
    const powers = estimateBandPowers(epoch, sampleRate);
    const denom = powers[denBand];
    out.push({
      timeSeconds,
      value: denom > 1e-12 ? powers[numBand] / denom : 0,
    });
  });
  return out;
}

/** Advanced quantitative biomarkers (Epic D5 / Gap #19). */
export type QEEGBiomarkerKind = "dar" | "tbr" | "str";

export const QEEG_BIOMARKER_LABELS: Record<QEEGBiomarkerKind, string> = {
  dar: "DAR (δ/α)",
  tbr: "TBR (θ/β)",
  str: "STR (σ/θ)",
};

export const QEEG_BIOMARKER_INTENDED_USE = {
  class: "research" as const,
  label: "Research / investigational — not a standalone diagnostic",
  forbiddenClaims: [
    "ADHD diagnosis from TBR alone",
    "Cognitive impairment diagnosis from DAR alone",
    "Sleep disorder diagnosis from STR/coherence alone",
    "FDA/CE cleared biomarker claim without validation",
  ],
  advisorReviewId: "EEG-T20-19-004",
};

const BIOMARKER_PAIR: Record<QEEGBiomarkerKind, [QEEGBandName, QEEGBandName]> = {
  dar: ["Delta", "Alpha"],
  tbr: ["Theta", "Beta"],
  str: ["Sigma", "Theta"],
};

export type BiomarkerSummary = {
  kind: QEEGBiomarkerKind;
  label: string;
  value: number;
  epochCount: number;
  provenance: QEEGTrendProvenance;
  intendedUseClass: "research";
  intendedUseLabel: string;
};

export function computeBiomarkerTrend(
  samples: Float32Array | number[],
  sampleRate: number,
  kind: QEEGBiomarkerKind,
  epochSeconds = 2,
): QEEGTrendSample[] {
  const [numBand, denBand] = BIOMARKER_PAIR[kind];
  const out: QEEGTrendSample[] = [];
  epochIterate(samples, sampleRate, epochSeconds, (epoch, timeSeconds) => {
    const powers = estimateBandPowers(epoch, sampleRate);
    const denom = powers[denBand];
    out.push({
      timeSeconds,
      value: denom > 1e-12 ? powers[numBand] / denom : 0,
    });
  });
  return out;
}

export function computeBiomarkerSummary(
  samples: Float32Array | number[],
  sampleRate: number,
  kind: QEEGBiomarkerKind,
  opts?: {
    montage?: string | null;
    filters?: Partial<QEEGFilterProvenance>;
    channelSet?: string[];
    epochSeconds?: number;
    generatedAt?: Date;
  },
): BiomarkerSummary {
  const epochSeconds = opts?.epochSeconds ?? 2;
  const trend = computeBiomarkerTrend(samples, sampleRate, kind, epochSeconds);
  const value =
    trend.length > 0
      ? trend.reduce((sum, s) => sum + s.value, 0) / trend.length
      : 0;
  return {
    kind,
    label: QEEG_BIOMARKER_LABELS[kind],
    value,
    epochCount: trend.length,
    provenance: buildQEEGTrendProvenance({
      channelSet: opts?.channelSet,
      montage: opts?.montage ?? null,
      samplingRateHz: sampleRate,
      filters: opts?.filters,
      epochSeconds,
      generatedAt: opts?.generatedAt,
      artifactExclusions: [`biomarker:${kind}`, "research_labeling"],
    }),
    intendedUseClass: "research",
    intendedUseLabel: QEEG_BIOMARKER_INTENDED_USE.label,
  };
}

export type SleepCoherenceBand = "delta" | "theta" | "sigma" | "beta";

export type SleepCoherenceSummary = {
  byStage: Record<
    string,
    { meanCoherence: number; bandMeans: Partial<Record<SleepCoherenceBand, number>>; n: number }
  >;
  overallMean: number;
  intendedUseClass: "research";
  provenanceNote: string;
};

/**
 * Sleep-stage coherence summary from precomputed pair/band values
 * (fixture- or analytics-backed; not a clinical sleep diagnosis).
 */
export function computeSleepStageCoherence(input: {
  pairSeries: Array<{
    stage: string;
    band: SleepCoherenceBand;
    coherence: number;
  }>;
}): SleepCoherenceSummary {
  type Acc = {
    sum: number;
    n: number;
    bands: Partial<Record<SleepCoherenceBand, { sum: number; n: number }>>;
  };
  const acc: Record<string, Acc> = {};
  let total = 0;
  let count = 0;
  for (const row of input.pairSeries) {
    const coh = Math.max(0, Math.min(1, row.coherence));
    if (!acc[row.stage]) acc[row.stage] = { sum: 0, n: 0, bands: {} };
    const bucket = acc[row.stage];
    bucket.sum += coh;
    bucket.n += 1;
    if (!bucket.bands[row.band]) bucket.bands[row.band] = { sum: 0, n: 0 };
    bucket.bands[row.band]!.sum += coh;
    bucket.bands[row.band]!.n += 1;
    total += coh;
    count += 1;
  }
  const byStage: SleepCoherenceSummary["byStage"] = {};
  for (const [stage, bucket] of Object.entries(acc)) {
    const bandMeans: Partial<Record<SleepCoherenceBand, number>> = {};
    for (const [band, stats] of Object.entries(bucket.bands) as Array<
      [SleepCoherenceBand, { sum: number; n: number }]
    >) {
      bandMeans[band] = stats.n > 0 ? stats.sum / stats.n : 0;
    }
    byStage[stage] = {
      meanCoherence: bucket.n > 0 ? bucket.sum / bucket.n : 0,
      bandMeans,
      n: bucket.n,
    };
  }
  return {
    byStage,
    overallMean: count > 0 ? total / count : 0,
    intendedUseClass: "research",
    provenanceNote:
      "Sleep-stage coherence is research/investigational; montage and filter provenance required before clinical use.",
  };
}

export function formatBiomarkerReportFields(
  summaries: BiomarkerSummary[],
): {
  fields: Array<{ code: string; label: string; value: string; intendedUseClass: "research" }>;
  disclaimer: string;
} {
  return {
    fields: summaries.map((s) => ({
      code: `biomarker_${s.kind}`,
      label: s.label,
      value: s.value.toFixed(3),
      intendedUseClass: "research" as const,
    })),
    disclaimer: QEEG_BIOMARKER_INTENDED_USE.label,
  };
}

/** Downsample a spectrogram matrix for DSA-style display. */
export function downsampleSpectrogramMatrix(
  matrix: number[][],
  maxColumns: number,
): number[][] {
  if (!matrix.length || maxColumns <= 0) return [];
  if (matrix[0].length <= maxColumns) return matrix.map((row) => [...row]);

  const cols = matrix[0].length;
  const step = cols / maxColumns;
  return matrix.map((row) => {
    const out: number[] = [];
    for (let c = 0; c < maxColumns; c += 1) {
      const start = Math.floor(c * step);
      const end = Math.min(cols, Math.floor((c + 1) * step));
      let sum = 0;
      let count = 0;
      for (let i = start; i < end; i += 1) {
        sum += Number(row[i] || 0);
        count += 1;
      }
      out.push(count ? sum / count : 0);
    }
    return out;
  });
}

/**
 * Map a DSA column index to absolute recording time for jump-to-raw linking.
 */
export function mapDsaColumnToTimeSeconds(
  columnIndex: number,
  columnCount: number,
  window: QEEGTrendWindow,
): number {
  if (columnCount <= 1) return window.startSeconds;
  const clamped = Math.max(0, Math.min(columnCount - 1, columnIndex));
  const t =
    window.startSeconds +
    (clamped / (columnCount - 1)) * window.durationSeconds;
  return Math.max(window.startSeconds, Math.min(window.endSeconds, t));
}

export function mapCursorToTrendIndex(
  currentTimeSeconds: number,
  samples: QEEGTrendSample[],
): number {
  if (!samples.length) return -1;
  let best = 0;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < samples.length; i += 1) {
    const dist = Math.abs(samples[i].timeSeconds - currentTimeSeconds);
    if (dist < bestDist) {
      best = i;
      bestDist = dist;
    }
  }
  return best;
}

/** Filter trend samples into the synchronized viewer window. */
export function filterTrendSamplesToWindow(
  samples: QEEGTrendSample[],
  window: QEEGTrendWindow,
): QEEGTrendSample[] {
  return samples.filter(
    (sample) =>
      sample.timeSeconds >= window.startSeconds &&
      sample.timeSeconds <= window.endSeconds,
  );
}

/** Cap sample count for UI/performance while preserving endpoints. */
export function downsampleTrendSamples(
  samples: QEEGTrendSample[],
  maxPoints: number,
): QEEGTrendSample[] {
  if (samples.length <= maxPoints || maxPoints < 2) return samples;
  const out: QEEGTrendSample[] = [];
  const step = (samples.length - 1) / (maxPoints - 1);
  for (let i = 0; i < maxPoints; i += 1) {
    out.push(samples[Math.round(i * step)]);
  }
  return out;
}

export function epochPowers(
  samples: Float32Array | number[] | undefined,
  sampleRate: number,
  epochSeconds = 1,
): number[] {
  if (!samples?.length) return [];
  const out: number[] = [];
  epochIterate(samples, sampleRate, epochSeconds, (epoch) => {
    let sum = 0;
    for (const v of epoch) sum += v * v;
    out.push(sum / epoch.length);
  });
  return out;
}

/**
 * DSA / CDSA matrix from band-power epochs (S1). Prefer this over any
 * synthetic stub; columns are real Goertzel band estimates per epoch.
 */
export function computeDsaMatrixFromSignal(
  samples: Float32Array | number[],
  sampleRate: number,
  epochSeconds = 2,
  maxColumns = 120,
): number[][] {
  const bands: QEEGBandName[] = ["Delta", "Theta", "Alpha", "Beta", "Gamma"];
  const columns: number[][] = bands.map(() => []);
  epochIterate(samples, sampleRate, epochSeconds, (epoch) => {
    const powers = estimateBandPowers(epoch, sampleRate);
    bands.forEach((band, index) => {
      columns[index].push(powers[band]);
    });
  });
  return downsampleSpectrogramMatrix(columns, maxColumns);
}

/** @deprecated Alias — use computeDsaMatrixFromSignal. */
export function synthesizeDsaMatrixFromSignal(
  samples: Float32Array | number[],
  sampleRate: number,
  epochSeconds = 2,
  maxColumns = 120,
): number[][] {
  return computeDsaMatrixFromSignal(
    samples,
    sampleRate,
    epochSeconds,
    maxColumns,
  );
}

export function toFiniteNumberArray(
  samples?: Float32Array | number[],
): number[] {
  if (!samples) return [];
  return toArray(samples).map((v) => (Number.isFinite(v) ? v : 0));
}
